import React, { useMemo } from 'react';

import CardDialog from '~/components/CardDialog';
import { CliveAvatar } from '~/components/Clive/CliveAvatar';
import CliveAlertDialog from '~/components/Clive/CliveDialog/CliveAlertDialog';
import { Heading, Text } from '~/components/core';
import { PolicyCard } from '~/components/Fnol/FnolAiInbox/PolicySelectionCard/PolicyCard';
import { PolicySearchCard } from '~/components/Fnol/FnolAiInbox/PolicySelectionCard/PolicySearchCard';
import type { Policy, PolicySearchValues, PolicyWithClaimsData } from '~/components/Fnol/FnolAiInbox/types';
import LoadingIndicator from '~/components/LoadingIndicator';
import useOrganization from '~/components/OrganizationContext';
import type { PolicySearchConfigLike } from '~/components/policySearchSource';
import { hasPolicySource } from '~/components/policySearchSource';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  FnolInboxApplicableSubOrgsResponse,
  FnolInboxRecordFnolInboxPolicy,
} from '~/server_shared/openapi/data-contracts';

type PolicySelectionCardState = 'loading' | 'show-policy' | 'search-policy' | 'change-policy-message';

interface PolicySelectionCardResponse {
  policy?: FnolInboxRecordFnolInboxPolicy;
}

interface PolicySelectionCardProps {
  inboxItemId: number;
  policyNumber?: string;
  onCancel: () => void;
  onSubmit: (
    inboxItemId: number,
    policy?: Policy,
    shouldShowChangePolicyMessage?: boolean,
    withClive?: boolean | null
  ) => Promise<void>;
  viewOnly: boolean;
  shouldShowChangePolicyMessage: boolean;
  constSearchValues?: PolicySearchValues;
  onCloseWithRefresh: () => void; // Called when CliveAlertDialog closes and data needs refresh
  withClive?: boolean | null; // Indicates if Clive is enabled for this component
  showTakeLossWithoutPolicy?: boolean;
}

export const PolicySelectionCard: React.FC<PolicySelectionCardProps> = ({
  inboxItemId,
  policyNumber,
  onCancel,
  onSubmit,
  shouldShowChangePolicyMessage,
  viewOnly = false,
  constSearchValues,
  onCloseWithRefresh,
  withClive = false,
  showTakeLossWithoutPolicy,
}) => {
  const { data, isLoading, isError } = useDataFetcher<PolicySelectionCardResponse>(
    `/api/v1/fnol_inbox/inbox/${inboxItemId}`,
    {},
    !!policyNumber
  );

  const [cardState, setCardState] = React.useState<PolicySelectionCardState>(
    policyNumber && withClive ? 'show-policy' : 'search-policy'
  );
  const [isProcessingWithoutPolicy, setIsProcessingWithoutPolicy] = React.useState(false);
  const { policySearchConfig }: { policySearchConfig?: PolicySearchConfigLike } = useOrganization();
  // No policy API and no explicit legacy DB opt-in: there is nothing to search, so the search
  // dialog would open onto an empty body.
  const hasNoPolicySource = !hasPolicySource(policySearchConfig);

  // Use useDataFetcher for applicable sub-organizations
  // Only fetch when we're in search-policy state, and only when there is a policy source to
  // search — the sub-org filter feeds the search card alone.
  const shouldFetchSubOrgs = cardState === 'search-policy' && !hasNoPolicySource;
  const {
    data: subOrgsData,
    isLoading: isLoadingSubOrgs,
    isError: isSubOrgsError,
  } = useDataFetcher<FnolInboxApplicableSubOrgsResponse>(
    `/api/v1/fnol_inbox/inbox/${inboxItemId}/applicable_sub_orgs`,
    {},
    shouldFetchSubOrgs
  );

  const policy = useMemo<Policy | undefined>(() => data?.policy?.policy_data as Policy, [data]);

  const allowedSubOrgIds = useMemo<number[] | undefined>(() => {
    if (isSubOrgsError) {
      // On error, don't restrict sub-orgs (allow all)
      return undefined;
    }
    return subOrgsData?.applicable_sub_organizations.map((subOrg) => subOrg.id);
  }, [subOrgsData, isSubOrgsError]);

  const onShowSearchPolicyHandler = () => {
    setCardState('search-policy');
  };

  const onSelectPolicyHandler = async (policyData: PolicyWithClaimsData) => {
    if (shouldShowChangePolicyMessage) {
      setCardState('change-policy-message');
    }

    // Extract just the policy for the FNOL inbox API (maintain original data structure)
    await onSubmit(inboxItemId, policyData.policy, shouldShowChangePolicyMessage, withClive);
    if (!shouldShowChangePolicyMessage) {
      onCancel();
    }
  };

  const onTakeLossWithoutPolicyHandler = async () => {
    // CASE 2: "Take Loss Without Policy" — send null policy to trigger reprocess without policy
    setIsProcessingWithoutPolicy(true);
    if (shouldShowChangePolicyMessage) {
      setCardState('change-policy-message');
    }
    await onSubmit(inboxItemId, undefined, shouldShowChangePolicyMessage, withClive);
    if (!shouldShowChangePolicyMessage) {
      onCancel();
    }
  };

  const handleCliveAlertDialogClose = React.useCallback(() => {
    onCloseWithRefresh();
  }, [onCloseWithRefresh]);

  React.useEffect(() => {
    if (isLoading) {
      setCardState('loading');
      return;
    }
    setCardState(policyNumber && withClive ? 'show-policy' : 'search-policy');
  }, [isLoading, policyNumber, withClive]);

  // Determine if we should show loading for the overall component
  const isOverallLoading = isLoading || (cardState === 'search-policy' && isLoadingSubOrgs);

  return (
    <>
      {isOverallLoading || isError ? (
        <CardDialog isDialog title="Loading Policy..." onClose={onCancel} maxWidth="sm">
          <LoadingIndicator isError={isError} />
        </CardDialog>
      ) : (
        <>
          {cardState === 'show-policy' && policy && (
            <PolicyCard
              policy={policy}
              onChangePolicy={onShowSearchPolicyHandler}
              onCancel={onCancel}
              viewOnly={viewOnly}
            />
          )}

          {cardState === 'search-policy' &&
            (hasNoPolicySource ? (
              <CardDialog isDialog title="Search Policy" onClose={onCancel} maxWidth="sm">
                <Text variant="small" weight="regular">
                  Policy search is not available for this organization.
                </Text>
              </CardDialog>
            ) : (
              <PolicySearchCard
                onSelectPolicy={onSelectPolicyHandler}
                onCancel={onCancel}
                onTakeLossWithoutPolicy={showTakeLossWithoutPolicy ? onTakeLossWithoutPolicyHandler : undefined}
                allowedSubOrgIds={allowedSubOrgIds}
                constSearchValues={constSearchValues}
              />
            ))}

          {cardState === 'change-policy-message' && (
            <CliveAlertDialog
              title={isProcessingWithoutPolicy ? 'Processing Without Policy' : 'Updating Policy'}
              onClose={handleCliveAlertDialogClose}
              isOpen={true}
            >
              <div className="flex flex-col gap-12">
                <Heading className="flex flex-col items-center gap-8 pb-4">
                  <CliveAvatar mood={CliveAvatar.MOOD.LOADING} size={80} />
                  <span className="text-2xl font-bold">Clive - Your AI Assistant</span>
                </Heading>
                <Text variant="small" weight="regular" className="px-32 text-center">
                  {isProcessingWithoutPolicy
                    ? 'I\u2019m currently processing the FNOL without a policy. This may take a few minutes. Once completed, the status will be updated.'
                    : 'I\u2019m currently processing the newly selected policy and updating the FNOL. This may take a few minutes. Once completed, the status will be updated.'}
                </Text>
              </div>
            </CliveAlertDialog>
          )}
        </>
      )}
    </>
  );
};
